import { lazy, Suspense } from "react";
import { Flex, Avatar, Menu, UnstyledButton, Tabs, Text, Burger, Drawer, Stack, NavLink, Box, Button } from "@mantine/core";
import { signOut, User } from "firebase/auth";
import { useTranslation } from "react-i18next";
import { auth } from "../services/firebase/config";
import { useLocation, useNavigate } from "react-router-dom";
import { useDisclosure } from "@mantine/hooks";

// Lazy - AppHeader renders on every page (including signed-out visitors who
// will never open this), so its firebase/auth surface (linkWithPopup,
// reauthenticateWithCredential, etc.) shouldn't sit in the main bundle.
const AccountSettingsModal = lazy(() =>
  import("./AccountSettingsModal").then((m) => ({ default: m.AccountSettingsModal }))
);

// Same reasoning - only ever rendered once currentUser exists, so it stays
// out of the bundle every signed-out visitor (the common case for most
// pages) actually downloads.
const ActivityBell = lazy(() => import("./ActivityBell").then((m) => ({ default: m.ActivityBell })));

export default function AppHeader({ currentUser, isAdmin }: { currentUser: User | undefined; isAdmin?: boolean }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpened, { toggle: toggleMenu, close: closeMenu }] = useDisclosure(false);
  const [isAccountSettingsOpen, { open: openAccountSettings, close: closeAccountSettings }] = useDisclosure(false);

  // Signed-in visitors manage memorials, not write their own story, so
  // "My Story" swaps out for "Memorials" once logged in; super admins also
  // get Submissions/Reports tabs for the moderation consoles.
  const mainMenu = currentUser
    ? [
        { title: t("account.menu.memorials"), url: "/account/memorials" },
        { title: t("main.tab.howItWorks"), url: "/how-it-works" },
        { title: t("main.tab.features"), url: "/features" },
        ...(isAdmin ? [{ title: t("admin.nav.monitor"), url: "/admin/monitor" }] : []),
      ]
    : [
        { title: t("main.tab.myStory"), url: "/my-story" },
        { title: t("main.tab.howItWorks"), url: "/how-it-works" },
        { title: t("main.tab.features"), url: "/features" },
      ];

  const isTabActive = (url: string) =>
    url === "/"
      ? location.pathname === "/"
      : location.pathname.startsWith(url === "/account/memorials" ? "/account/memorial" : url);
  const activeTab = mainMenu.find((m) => isTabActive(m.url))?.url ?? null;

  const goTo = (url: string) => {
    closeMenu();
    if (location.pathname !== url) {
      navigate(url);
    }
  };

  const logUserOut = () =>
    signOut(auth)
      .then(() => {
        closeMenu();
        navigate("/");
      })
      .catch((error) => {
        console.error(error.message);
      });

  return (
    <Flex justify="space-between" align="center" py="xs" px={{ base: "md", sm: "xl" }}>
      <Flex gap="xl" justify="center" align="center">
        <UnstyledButton onClick={() => goTo("/")}>
          {/* Site-wide chrome, not page content - a Text, not a Title, so
              it's never an h1/h2 competing with the actual page heading
              (this renders on every route, including ones whose own h1 is
              the whole reason a search engine would rank that page). */}
          <Text component="span" fz={{ base: "h3", sm: "h1" }} fw="bold" style={{ fontFamily: "var(--mantine-font-family-headings)" }}>
            {t("general.name")}
          </Text>
        </UnstyledButton>

        <Tabs variant="pills" value={activeTab} onChange={(v) => v && goTo(v)} visibleFrom="sm">
          <Tabs.List>
            {mainMenu.map((m) => (
              <Tabs.Tab key={m.title} fz="lg" value={m.url}>
                {m.title}
              </Tabs.Tab>
            ))}
          </Tabs.List>
        </Tabs>
      </Flex>

      <Flex align="center" gap="sm">
        {/* Shown on every page, not just visibleFrom="sm" like the avatar
            below - a phone visitor is just as much an owner/admin as a
            desktop one, and the drawer it opens is already full-screen on
            phones (see ActivityBell's own responsive size). */}
        {currentUser && (
          <Suspense fallback={null}>
            <ActivityBell currentUser={currentUser} />
          </Suspense>
        )}

        {currentUser && (
          <Box visibleFrom="sm">
            <Menu shadow="md" width={200} trigger="hover" position="bottom-end" offset={8} withArrow arrowPosition="center">
              <Menu.Target>
                <Avatar
                  key={currentUser.displayName}
                  name={currentUser.displayName ?? "QR"}
                  color="initials"
                  allowedInitialsColors={["brand-blue", "brand-gold", "brand-gray"]}
                  style={{ cursor: "pointer" }}
                />
              </Menu.Target>

              <Menu.Dropdown>
                <Menu.Item onClick={openAccountSettings}>{t("account.menu.account")}</Menu.Item>
                <Menu.Item onClick={() => goTo("/account/notifications")}>{t("account.menu.notifications")}</Menu.Item>
                <Menu.Item color="red" onClick={logUserOut}>
                  {t("account.menu.logout")}
                </Menu.Item>
              </Menu.Dropdown>
            </Menu>
          </Box>
        )}

        {!currentUser && (
          <Box visibleFrom="sm">
            <Button variant="light" radius="xl" onClick={() => goTo("/login")}>
              {t("main.tab.login")}
            </Button>
          </Box>
        )}

        <Burger opened={menuOpened} onClick={toggleMenu} hiddenFrom="sm" size="sm" />
      </Flex>

      <Drawer
        opened={menuOpened}
        onClose={closeMenu}
        position="right"
        size="xs"
        title={t("general.name")}
        overlayProps={{ backgroundOpacity: 0.6, blur: 4 }}
      >
        <Stack gap="xs">
          {mainMenu.map((m) => (
            <NavLink key={m.title} label={m.title} fz="lg" active={activeTab === m.url} onClick={() => goTo(m.url)} />
          ))}

          {currentUser ? (
            <>
              <NavLink
                label={t("account.menu.account")}
                fz="lg"
                onClick={() => {
                  closeMenu();
                  openAccountSettings();
                }}
              />
              <NavLink label={t("account.menu.notifications")} fz="lg" onClick={() => goTo("/account/notifications")} />
              <NavLink label={t("account.menu.logout")} c="red" onClick={logUserOut} />
            </>
          ) : (
            <NavLink label={t("main.tab.login")} onClick={() => goTo("/login")} />
          )}
        </Stack>
      </Drawer>

      {currentUser && isAccountSettingsOpen && (
        <Suspense fallback={null}>
          <AccountSettingsModal opened={isAccountSettingsOpen} onClose={closeAccountSettings} currentUser={currentUser} />
        </Suspense>
      )}
    </Flex>
  );
}
